import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { CalendarIcon, UserIcon, StethoscopeIcon, CreditCardIcon, FlaskConicalIcon, PillIcon, PackageIcon, BarChart3Icon, SparklesIcon, TrendingUpIcon, UsersIcon, ActivityIcon } from 'lucide-react';
const modules = [{
  path: '/appointments',
  title: 'Appointment Management',
  description: 'Schedule and manage appointments across web, mobile, and phone',
  icon: CalendarIcon,
  color: 'bg-blue-500',
  stats: {
    label: "Today's Appointments",
    value: '24'
  }
}, {
  path: '/patient-portal',
  title: 'Patient Portal',
  description: 'Complete health lifecycle management for patients',
  icon: UserIcon,
  color: 'bg-green-500',
  stats: {
    label: 'Active Patients',
    value: '1,234'
  }
}, {
  path: '/doctor-portal',
  title: 'Doctor Portal',
  description: 'Doctor profiles, calendars, and consultation workflows',
  icon: StethoscopeIcon,
  color: 'bg-purple-500',
  stats: {
    label: 'Available Doctors',
    value: '18'
  }
}, {
  path: '/billing',
  title: 'Billing & Payments',
  description: 'Manage payments, insurance, collections, and tax',
  icon: CreditCardIcon,
  color: 'bg-yellow-500',
  stats: {
    label: 'Pending Payments',
    value: '₹45,000'
  }
}, {
  path: '/pathology',
  title: 'Pathology Lab',
  description: 'Lab and blood collection management system',
  icon: FlaskConicalIcon,
  color: 'bg-red-500',
  stats: {
    label: 'Tests Today',
    value: '56'
  }
}, {
  path: '/pharmacy',
  title: 'Pharmacy',
  description: 'Integrated chemist and pharmacy management',
  icon: PillIcon,
  color: 'bg-pink-500',
  stats: {
    label: 'Prescriptions',
    value: '89'
  }
}, {
  path: '/vendors',
  title: 'Vendor Management',
  description: 'Manage vendors and supplies efficiently',
  icon: PackageIcon,
  color: 'bg-orange-500',
  stats: {
    label: 'Active Vendors',
    value: '32'
  }
}, {
  path: '/analytics',
  title: 'Reporting & Analytics',
  description: 'Comprehensive insights and performance metrics',
  icon: BarChart3Icon,
  color: 'bg-indigo-500',
  stats: {
    label: 'Revenue Growth',
    value: '+12%'
  }
}];
const quickStats = [{
  label: 'Total Patients',
  value: '1,234',
  icon: UsersIcon,
  change: '+8%'
}, {
  label: "Today's Revenue",
  value: '₹1,24,500',
  icon: TrendingUpIcon,
  change: '+15%'
}, {
  label: 'Active Sessions',
  value: '42',
  icon: ActivityIcon,
  change: '+5%'
}, {
  label: 'Satisfaction Rate',
  value: '98%',
  icon: SparklesIcon,
  change: '+2%'
}];
export function Dashboard() {
  const navigate = useNavigate();
  return <div className="min-h-screen bg-gray-50 w-full" data-id="element-1784">
      <Navigation data-id="element-1785" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1786">
        {/* Header */}
        <div className="mb-8" data-id="element-1787">
          <div className="flex items-center space-x-2 mb-2" data-id="element-1788">
            <SparklesIcon className="w-6 h-6 text-blue-600" data-id="element-1789" />
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-1790">
              AI-Native Clinic ERP
            </h1>
          </div>
          <p className="text-gray-600" data-id="element-1791">
            Comprehensive healthcare management system powered by AI
          </p>
        </div>
        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-1792">
          {quickStats.map(stat => {
          const Icon = stat.icon;
          return <div key={stat.label} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1793">
                <div className="flex items-center justify-between mb-2" data-id="element-1794">
                  <Icon className="w-5 h-5 text-gray-400" data-id="element-1795" />
                  <span className="text-sm font-medium text-green-600" data-id="element-1796">
                    {stat.change}
                  </span>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-1797">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600" data-id="element-1798">{stat.label}</div>
              </div>;
        })}
        </div>
        {/* Modules Grid */}
        <div className="mb-6" data-id="element-1799">
          <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-1800">
            System Modules
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1801">
          {modules.map(module => {
          const Icon = module.icon;
          return <button key={module.path} onClick={() => navigate(module.path)} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-left hover:shadow-md transition-shadow group" data-id="element-1802">
                <div className="flex items-start justify-between mb-4" data-id="element-1803">
                  <div className={`${module.color} rounded-lg p-3`} data-id="element-1804">
                    <Icon className="w-6 h-6 text-white" data-id="element-1805" />
                  </div>
                  <div className="text-right" data-id="element-1806">
                    <div className="text-2xl font-bold text-gray-900" data-id="element-1807">
                      {module.stats.value}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-1808">
                      {module.stats.label}
                    </div>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors" data-id="element-1809">
                  {module.title}
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1810">{module.description}</p>
              </button>;
        })}
        </div>
        {/* AI Features Banner */}
        <div className="mt-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-lg p-6 text-white" data-id="element-1811">
          <div className="flex items-center space-x-3 mb-2" data-id="element-1812">
            <SparklesIcon className="w-6 h-6" data-id="element-1813" />
            <h3 className="text-xl font-semibold" data-id="element-1814">AI-Powered Features</h3>
          </div>
          <p className="text-blue-50 mb-4" data-id="element-1815">
            Intelligent appointment scheduling, predictive analytics, automated
            billing, smart inventory management, and AI-assisted diagnostics
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-1816">
            <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm" data-id="element-1817">
              Smart Scheduling
            </span>
            <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm" data-id="element-1818">
              Predictive Analytics
            </span>
            <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm" data-id="element-1819">
              Automated Workflows
            </span>
            <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm" data-id="element-1820">
              AI Diagnostics
            </span>
          </div>
        </div>
      </div>
    </div>;
}